import { usePagination } from '@mantine/hooks';
import { BaseRecord, useTable as useRefineTable } from '@refinedev/core';
import { useEffect } from 'react';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { TableData } from './useTable';

type PaginationResponse<T> = {
  data: T[];
  total: number;
};

export const useServerPaginatedTable = <T extends BaseRecord>(
  resource: string,
  pageSize: number,
): TableData<T> => {
  const { tableQuery, pageCount, current, setCurrent } = useRefineTable<PaginationResponse<T>>({
    resource,
    pagination: { current: 1, pageSize, mode: 'server' },
    syncWithLocation: true,
  });

  useEffect(() => {
    if (tableQuery.isError && tableQuery.error) {
      notifyAndLogError(
        'Failed to load data',
        'An error occurred while loading table data. Please try again later.',
        tableQuery.error,
        { resource, current, pageSize },
      );
    }
  }, [tableQuery.isError, tableQuery.error, resource, current, pageSize]);

  const pagination = usePagination({
    total: pageCount,
    page: current,
    onChange: (newPage: number) => {
      try {
        setCurrent(newPage);
      } catch (error) {
        notifyAndLogError(
          'Navigation failed',
          'Failed to navigate to the requested page. Please try again.',
          error,
          { resource, current, newPage },
        );
      }
    },
  });

  let processedData: T[] = [];
  let processedTotalRecords = 0;

  try {
    const responseData = tableQuery.data as PaginationResponse<T> | T[] | undefined;
    processedData = Array.isArray(responseData) ? responseData : responseData?.data || [];
    processedTotalRecords = Array.isArray(responseData)
      ? responseData.length
      : responseData?.total || 0;
  } catch (error) {
    notifyAndLogError(
      'Data processing failed',
      'An error occurred while processing table data. Please refresh the page.',
      error,
      { resource, responseData: tableQuery.data },
    );
  }

  return {
    data: processedData,
    isLoading: tableQuery.isLoading,
    isFetching: tableQuery.isFetching,
    isError: tableQuery.isError,
    error: tableQuery.error,
    pagination,
    totalRecords: processedTotalRecords,
    refetch: () => {
      try {
        return tableQuery.refetch();
      } catch (error) {
        notifyAndLogError(
          'Refresh failed',
          'Failed to refresh table data. Please try again.',
          error,
          { resource },
        );
        throw error;
      }
    },
    sortStatus: undefined,
    onSortStatusChange: undefined,
  };
};
